import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_PERMISSIONS = {
  'Property Manager': [
    'view_building_info',
    'manage_tenants',
    'view_lease_agreements',
    'edit_lease_agreements',
    'view_asset_details',
    'edit_asset_details',
    'assign_assets',
    'view_reports',
    'generate_reports',
    'access_dashboard',
    'manage_users',
    'VIEW_SERVICE_LOGS',
    'EDIT_SERVICE_LOGS',
    'VIEW_INCIDENTS',
    'EDIT_INCIDENTS',
    'VIEW_EMPLOYEE_ENGAGEMENT',
    'EDIT_EMPLOYEE_ENGAGEMENT',
    'VIEW_GEOLOCATION',
    'EDIT_GEOLOCATION',
    'VIEW_MESSAGES',
    'EDIT_MESSAGES',
    'manage_visitors',
    'view_visitors',
    'checkin_visitors',
    'approve_visitors'
  ],
  'Retail Manager': [
    'view_building_info',
    'edit_building_info',
    'VIEW_MESSAGES'
  ],
  'Asset Manager': [
    'view_building_info',
    'edit_building_info',
    'view_lease_agreements',
    'edit_lease_agreements',
    'view_asset_details',
    'edit_asset_details',
    'assign_assets',
    'view_reports',
    'generate_reports',
    'access_dashboard',
    'manage_users',
    'VIEW_SERVICE_LOGS',
    'EDIT_SERVICE_LOGS',
    'VIEW_INCIDENTS',
    'EDIT_INCIDENTS',
    'VIEW_EMPLOYEE_ENGAGEMENT',
    'EDIT_EMPLOYEE_ENGAGEMENT',
    'VIEW_GEOLOCATION',
    'EDIT_GEOLOCATION',
    'VIEW_MESSAGES',
    'EDIT_MESSAGES'
  ],
  'Technician': [
    'VIEW_SERVICE_LOGS',
    'EDIT_SERVICE_LOGS',
    'VIEW_ASSET_DETAILS',
    'VIEW_INCIDENTS',
    'VIEW_MESSAGES',
    'access_dashboard',
    'view_assignments',
    'update_work_status',
    'view_technician_schedule'
  ],
  'Maintenance': [
    'VIEW_SERVICE_LOGS',
    'EDIT_SERVICE_LOGS',
    'VIEW_ASSET_DETAILS',
    'VIEW_INCIDENTS',
    'VIEW_MESSAGES'
  ],
  'Maintenance Staff': [
    'VIEW_SERVICE_LOGS',
    'EDIT_SERVICE_LOGS',
    'VIEW_ASSET_DETAILS',
    'VIEW_INCIDENTS',
    'VIEW_MESSAGES',
    'access_dashboard',
    'view_assignments',
    'update_work_status'
  ],
  'Service Provider': [
    'VIEW_SERVICE_LOGS',
    'VIEW_ASSET_DETAILS',
    'VIEW_INCIDENTS',
    'VIEW_MESSAGES',
    'access_dashboard',
    'view_service_requests',
    'submit_proposals',
    'view_provider_profile'
  ]
};

const getRedirectPath = (userRole) => {
  switch (userRole) {
    case 'Retail Manager':
      return '/properties';
    case 'Technician':
    case 'Maintenance Staff':
      return '/dashboard'; // Technician dashboard with assignments
    case 'Service Provider':
      return '/dashboard'; // Service provider dashboard with requests
    case 'Property Manager':
    case 'Asset Manager':
    default:
      return '/dashboard';
  }
};

const AuthContext = createContext({
  user: null,
  permissions: [],
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: () => {},
  signup: async () => {},
  hasPermission: () => false,
  checkSession: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.defaults.baseURL = BACKEND_URL;
    axios.defaults.withCredentials = true;
    
    const requestInterceptor = axios.interceptors.request.use((config) => {
      config.withCredentials = true;
      return config;
    });
    
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('🚨 401 Unauthorized - clearing auth state');
          setUser(null);
          setPermissions([]);
          setIsAuthenticated(false);
          Cookies.remove('access_token');
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      setIsLoading(true);
      
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount < maxRetries) {
        try {
          console.log(`🔄 Checking session (attempt ${retryCount + 1}/${maxRetries})`);
          const response = await axios.get('/api/auth/check-session');
          
          if (response.data.valid) {
            setUser(response.data.user);
            setPermissions(response.data.permissions);
            setIsAuthenticated(true);
            console.log('✅ Session valid:', {
              user: response.data.user.email,
              role: response.data.user.role,
              permissions: response.data.permissions.length
            });
            return;
          } else {
            setUser(null);
            setPermissions([]);
            setIsAuthenticated(false);
            console.log('❌ Session invalid');
            return;
          }
        } catch (error) {
          retryCount++;
          console.log(`❌ Session check attempt ${retryCount} failed:`, error.response?.status, error.message);
          
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            console.log('❌ Session check failed after all retries');
            setUser(null);
            setPermissions([]);
            setIsAuthenticated(false);
            Cookies.remove('access_token');
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      const { user: userData } = response.data;
      console.log('✅ Login successful:', userData.email);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          console.log(`🔄 Attempting to load permissions (attempt ${retryCount + 1}/${maxRetries})`);
          const permsResponse = await axios.get('/api/auth/me/permissions');
          const actualPermissions = permsResponse.data.permissions || [];
          
          setUser(userData);
          setPermissions(actualPermissions);
          setIsAuthenticated(true);
          
          console.log('✅ Permissions loaded successfully:', actualPermissions.length);
          
          return { success: true, user: userData, redirectPath: getRedirectPath(userData.role) };
        } catch (permError) {
          retryCount++;
          console.log(`❌ Permissions load attempt ${retryCount} failed:`, permError.response?.status, permError.message);
          
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          } else {
            console.error('❌ Failed to load permissions after all retries, using fallback');
            setUser(userData);
            setPermissions(ROLE_PERMISSIONS[userData.role] || []);
            setIsAuthenticated(true);
            
            return { success: true, user: userData, redirectPath: getRedirectPath(userData.role) };
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Login error:', error);
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post('/api/auth/signup', userData);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Signup error:', error);
      
      let message = 'Registration failed';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          });
          message = validationErrors.join(', ');
        }
        // Handle single validation error object
        else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
          message = errorData.detail.msg;
        }
        // Handle string error messages
        else if (typeof errorData.detail === 'string') {
          message = errorData.detail;
        }
        // Handle direct error message
        else if (typeof errorData === 'string') {
          message = errorData;
        }
      }
      
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setPermissions([]);
      setIsAuthenticated(false);
      Cookies.remove('access_token');
    }
  };

  const hasPermission = (permission) => {
    const hasIt = permissions.includes(permission);
    console.log(`🔍 Permission check: "${permission}" -> ${hasIt}`, {
      userPermissions: permissions,
      userRole: user?.role
    });
    return hasIt;
  };

  const hasRole = (role) => {
    const hasIt = user?.role === role;
    console.log(`🔍 Role check: "${role}" -> ${hasIt}`, {
      userRole: user?.role
    });
    return hasIt;
  };

  const value = {
    user,
    permissions,
    isAuthenticated,
    isLoading,
    login,
    logout,
    signup,
    hasPermission,
    hasRole,
    checkSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};